import { createSelector } from '@ngrx/store';
import { StepperConfig } from 'qbe-components';
import { ApiState } from 'src/app/model/api-state.model';
import { STEPS_CONFIG } from '../../config/step.config';
import { environment } from '../../environments/environment';
import { AppType } from '../../model/app-type.model';
import { FormsResignation, FormsTravelInPl, FormsTripAbroad } from '../../model/forms.model';
import { CalculationAddOns, PersonsWith } from '../../model/offer';
import { ProcessType } from '../../model/process-type.model';
import { StepConfigSingle } from '../../model/step-config.model';
import { PersistStorageKeys } from '../../services/persist-storage.service';
import { clamp, formatApiAddOns, getAge, getIndexes, isEmpty, transformUrl } from '../../utils';
import { LEGAL_AGE } from '../../utils/getAge.util';
import { AppState } from '../index';
import * as fromProcess from './process.reducer';

const STEP_DIFF = 1;
const STEP_MIN = 0;
const STEP_DIRECT_MAX = getIndexes(STEPS_CONFIG());
const STEP_AGENT_MAX = getIndexes(STEPS_CONFIG());
const STEP_DIRECT_VISIBLE_MAX = 6;
const STEP_AGENT_VISIBLE_MAX = 8;
const STEP_DIRECT_OFFER_MIN = 2;
const STEP_AGENT_OFFER_MIN = 2;
const STEP_DIRECT_HELP_MODAL_MAX = 1;
const STEP_AGENT_HELP_MODAL_MAX = 1;
const PACKAGE_INDEX_MIN = 0;
const STEP_LABEL_INDEX_1 = 0;
const STEP_LABEL_INDEX_2 = 1;
const STEP_LABEL_INDEX_3 = 2;
const STEP_LABEL_INDEX_4 = 3;

export const selectProcessStep = createSelector(
  (state: AppState) => state.process,
  ({ step }: fromProcess.State) => step,
);

export const selectProcessKind = createSelector(
  (state: AppState) => state.process,
  ({ kind }: fromProcess.State) => kind,
);

export const selectProcessIsSuccess = createSelector(
  (state: AppState) => state.process,
  ({ isSuccess }: fromProcess.State) => isSuccess,
);

export const selectProcessConclusion = createSelector(
  (state: AppState) => state.process,
  ({ conclusion }: fromProcess.State) => conclusion,
);

export const selectProcessStepperConfig = createSelector(
  (state: AppState) => state.process,
  ({ step }: fromProcess.State): StepperConfig => {
    const stepData: StepConfigSingle | null = STEPS_CONFIG()[step];

    if (!stepData) {
      return {
        totalSteps: 0,
        currentStepIndex: 0,
        currentProgress: 0,
        stepsLabels: [],
        customColors: {
          progressBar: {
            background: 'rgba(0, 97, 146, 0.2)',
          },
          stepper: {
            step: 'rgba(0, 97, 146, 0.2)',
            label: 'rgba(0, 97, 146, 0.2)',
          },
        },
      };
    }

    return {
      totalSteps: 4,
      currentStepIndex: stepData.index,
      currentProgress: stepData.progress,
      stepsLabels: [
        STEPS_CONFIG()?.find((stepConf) => stepConf.index === STEP_LABEL_INDEX_1)?.label ?? 'progress-stepper-labels.step1',
        STEPS_CONFIG()?.find((stepConf) => stepConf.index === STEP_LABEL_INDEX_2)?.label ?? 'progress-stepper-labels.step2',
        STEPS_CONFIG()?.find((stepConf) => stepConf.index === STEP_LABEL_INDEX_3)?.label ?? 'progress-stepper-labels.step3',
        STEPS_CONFIG()?.find((stepConf) => stepConf.index === STEP_LABEL_INDEX_4)?.label ?? 'progress-stepper-labels.step4',
      ],
      customColors: {
        progressBar: {
          background: 'rgba(0, 97, 146, 0.2)',
        },
        stepper: {
          step: 'rgba(0, 97, 146, 0.2)',
          label: 'rgba(0, 97, 146, 0.2)',
        },
      },
    };
  },
);

export const selectProcessPrevStep = createSelector(
  (state: AppState) => state.process,
  ({ step }: fromProcess.State) => {
    switch (environment.app) {
      case AppType.Direct:
        return clamp(step, STEP_MIN, STEP_DIRECT_MAX);
      default:
        return clamp(step, STEP_MIN, STEP_AGENT_MAX);
    }
  },
);

export const selectProcessPrevStepUrl = createSelector(
  (state: AppState) => state.process,
  selectProcessPrevStep,
  ({ conclusion }: fromProcess.State, prevStep) => {
    try {
      return transformUrl(STEPS_CONFIG()[prevStep - STEP_DIFF].route, { path: { conclusion } });
    } catch (e) {
      return transformUrl('/error');
    }
  },
);

export const selectProcessNextStep = createSelector(
  (state: AppState) => state.process,
  ({ step }: fromProcess.State) => {
    switch (environment.app) {
      case AppType.Direct:
        return clamp(step + STEP_DIFF, STEP_MIN, STEP_DIRECT_MAX);
      default:
        return clamp(step + STEP_DIFF, STEP_MIN, STEP_AGENT_MAX);
    }
  },
);

export const selectProcessNextStepUrl = createSelector(
  (state: AppState) => state.process,
  selectProcessNextStep,
  ({ conclusion }: fromProcess.State, nextStep) => {
    try {
      return transformUrl(STEPS_CONFIG()[nextStep].route, { path: { conclusion } });
    } catch (e) {
      return transformUrl('/error');
    }
  },
);

export const selectProcessPrevAvailable = createSelector(
  (state: AppState) => state.process,
  ({ step, isSuccess }: fromProcess.State) => step > STEP_MIN && !isSuccess,
);

export const selectProcessNextVisible = createSelector(
  (state: AppState) => state.process,
  ({ step }: fromProcess.State) => {
    switch (environment.app) {
      case AppType.Direct:
        return step < STEP_DIRECT_VISIBLE_MAX;
      default:
        return step < STEP_AGENT_VISIBLE_MAX;
    }
  },
);

export const selectProcessHelpModalVisible = createSelector(
  (state: AppState) => state.process,
  ({ step }: fromProcess.State) => {
    switch (environment.app) {
      case AppType.Direct:
        return step <= STEP_DIRECT_HELP_MODAL_MAX;
      default:
        return step <= STEP_AGENT_HELP_MODAL_MAX;
    }
  },
);

export const selectProcessOfferVisible = createSelector(
  (state: AppState) => state.process,
  ({ step }: fromProcess.State) => {
    switch (environment.app) {
      case AppType.Direct:
        return step >= STEP_DIRECT_OFFER_MIN;
      default:
        return step >= STEP_AGENT_OFFER_MIN;
    }
  },
);

export const selectProcessSavedCalculationState = createSelector(
  (state: AppState) => state.process,
  ({ savedCalculationState }: fromProcess.State) => savedCalculationState,
);

export const selectProcessSavedCalculationPayload = createSelector(
  (state: AppState) => state.process,
  ({ savedCalculationPayload }: fromProcess.State) => savedCalculationPayload,
);

export const selectProcessSavedCalculationError = createSelector(
  (state: AppState) => state.process,
  ({ savedCalculationError }: fromProcess.State) => savedCalculationError,
);

export const selectProcessHashCode = createSelector(
  (state: AppState) => state.process,
  ({ savedCalculation }: fromProcess.State) => savedCalculation?.result?.hashCode,
);
export const selectSavedCalculation = createSelector(
  (state: AppState) => state.process,
  ({ savedCalculation }: fromProcess.State) => savedCalculation,
);

export const selectProcessSavedOfferPolicyNumber = createSelector(
  (state: AppState) => state.process,
  ({ savedOfferCalculation }: fromProcess.State) => savedOfferCalculation?.result?.policyNumber,
);

export const selectProcessSavedOfferState = createSelector(
  (state: AppState) => state.process,
  ({ savedOfferCalculationState }: fromProcess.State) => savedOfferCalculationState,
);

export const selectProcessSavedOfferError = createSelector(
  (state: AppState) => state.process,
  ({ savedOfferCalculationError }: fromProcess.State) => savedOfferCalculationError,
);

export const selectProcessConfirmState = createSelector(
  (state: AppState) => state.process,
  ({ confirmState }: fromProcess.State) => confirmState,
);

export const selectProcessPackageName = createSelector(
  (state: AppState) => state.process,
  ({ selectedPackage }: fromProcess.State) => selectedPackage,
);

export const selectProcessConclusionState = createSelector(
  (state: AppState) => state.process,
  ({ conclusionState }: fromProcess.State) => conclusionState,
);

export const selectProcessConclusionError = createSelector(
  (state: AppState) => state.process,
  ({ conclusionError }: fromProcess.State) => conclusionError,
);

export const selectProcessConclusionData = createSelector(
  (state: AppState) => state.process,
  ({ conclusionData }: fromProcess.State) => conclusionData,
);

export const selectProcessNavigationError = createSelector(
  (state: AppState) => state.process,
  ({ navigationError }: fromProcess.State) => navigationError,
);

export const selectProcessFormOfPayment = createSelector(
  (state: AppState) => state.process,
  ({ formOfPayment }: fromProcess.State) => formOfPayment,
);

export const selectSendStaticAttachmentsState = createSelector(
  (state: AppState) => state.process,
  ({ sendStaticAttachmentsState }: fromProcess.State) => sendStaticAttachmentsState,
);

export const selectSendBackOfficeData = createSelector(
  (state: AppState) => state.process,
  ({ sendBackOfficeData }: fromProcess.State) => sendBackOfficeData,
);

export const selectSendStaticAttachmentsStatus = createSelector(
  (state: AppState) => state.process,
  ({ sendStaticAttachmentsData }: fromProcess.State) => {
    if (sendStaticAttachmentsData?.message) {
      switch (sendStaticAttachmentsData.message) {
        case 'Success':
          return ApiState.success;
        default:
          return ApiState.error;
      }
    } else {
      return ApiState.idle;
    }
  },
);

export const selectCalculationToClientConfirmState = createSelector(
  (state: AppState) => state.process,
  ({ calculationToClientConfirmState }: fromProcess.State) => calculationToClientConfirmState,
);

export const selectCalculationToClientConfirmData = createSelector(
  (state: AppState) => state.process,
  ({ calculationToClientConfirmData }: fromProcess.State) => calculationToClientConfirmData,
);

export const selectCalculationToClientConfirmError = createSelector(
  (state: AppState) => state.process,
  ({ calculationToClientConfirmError }: fromProcess.State) => calculationToClientConfirmError,
);
export const selectOfferQuotationState = createSelector(
  ({ process }: AppState) => process,
  ({ quotationState }: fromProcess.State) => quotationState,
);

export const selectOfferQuotationData = createSelector(
  ({ process }: AppState) => process,
  ({ quotationData }: fromProcess.State) => quotationData,
);

export const selectOfferQuotationError = createSelector(
  ({ process }: AppState) => process,
  ({ quotationError }: fromProcess.State) => quotationError,
);

export const selectOfferPackages = createSelector(
  ({ process }: AppState) => process,
  ({ quotationData, kind }: fromProcess.State) => (kind && quotationData && quotationData?.result?.path?.[kind]?.packages) || null,
);

export const selectSelectedPackageName = createSelector(
  ({ process }: AppState) => process,
  ({ selectedPackage }: fromProcess.State) => selectedPackage,
);

export const selectOfferSelectedPackage = createSelector(
  ({ process }: AppState) => process,
  selectOfferPackages,
  ({ selectedPackage }: fromProcess.State, offerPackages) => offerPackages?.find(({ codeName }) => codeName === selectedPackage) || null,
);

export const selectOfferSelectedPackageIndex = createSelector(
  ({ process }: AppState) => process,
  selectOfferPackages,
  selectOfferSelectedPackage,
  (_: fromProcess.State, offerPackages, selectedPackage) => {
    const index = offerPackages ? offerPackages.findIndex(({ codeName }) => codeName === selectedPackage?.codeName) : PACKAGE_INDEX_MIN;

    return clamp(index, PACKAGE_INDEX_MIN, getIndexes(offerPackages || []));
  },
);

export const selectOfferDiscountCodeState = createSelector(
  ({ process }: AppState) => process,
  ({ discountCodeState }: fromProcess.State) => discountCodeState,
);

export const selectOfferDiscountCodeData = createSelector(
  ({ process }: AppState) => process,
  ({ discountCodeData }: fromProcess.State) => discountCodeData,
);

export const selectOfferDiscountCodeError = createSelector(
  ({ process }: AppState) => process,
  ({ discountCodeError }: fromProcess.State) => discountCodeError,
);

export const selectOfferDiscountCode = createSelector(
  ({ process }: AppState) => process,
  ({ discountCode }: fromProcess.State) => discountCode,
);

export const selectOfferDiscountCodeIsValid = createSelector(
  ({ process }: AppState) => process,
  ({ discountCodeData }: fromProcess.State) => discountCodeData?.result.isValid === true,
);

export const selectPremiumCalculationState = createSelector(
  ({ process }: AppState) => process,
  ({ premiumCalculationState }: fromProcess.State) => {
    const apiStates = Object.values(premiumCalculationState);

    if (isEmpty(apiStates)) {
      return ApiState.idle;
    } else if (apiStates.includes(ApiState.pending)) {
      return ApiState.pending;
    } else if (apiStates.includes(ApiState.error)) {
      return ApiState.error;
    } else {
      return ApiState.success;
    }
  },
);

export const selectPremiumCalculationData = createSelector(
  ({ process }: AppState) => process,
  ({ premiumCalculationData }: fromProcess.State) => premiumCalculationData,
);

export const selectPremiumCalculationInsureds = createSelector(
  ({ process }: AppState) => process,
  ({ premiumCalculationData, selectedPackage }: fromProcess.State) => premiumCalculationData[selectedPackage]?.result.insureds,
);

export const selectQuotationInsureds = createSelector(
  ({ process }: AppState) => process,
  ({ quotationData }: fromProcess.State) => quotationData?.result.insureds,
);

export const selectPremiumPercentIncrease = createSelector(
  ({ process }: AppState) => process,
  ({ agentSection }: fromProcess.State) => agentSection.premiumPercentIncrease,
);

export const selectOfferValue = createSelector(
  ({ process }: AppState) => process,
  ({ selectedOfferValue }: fromProcess.State) => selectedOfferValue,
);

export const selectOfferValueBeforeDiscounts = createSelector(
  ({ process }: AppState) => process,
  ({ selectedOfferValueBeforeDiscounts }: fromProcess.State) => selectedOfferValueBeforeDiscounts,
);

export const selectCustomizeAlcoholIncluded = createSelector(
  ({ process }: AppState) => process,
  selectPremiumCalculationInsureds,
  selectQuotationInsureds,
  ({ kind, customizeAlcoholIncluded, packageAddons }: fromProcess.State, premiumInsureds, quotationInsureds): PersonsWith[] | null => {
    if (!premiumInsureds && !quotationInsureds) {
      return null;
    }

    const insureds = quotationInsureds ?? premiumInsureds;

    const isAlcoholIncluded = (id: number, birthYear: number): boolean => {
      const customizeAlcohol = customizeAlcoholIncluded?.find(({ id: selectedId }) => selectedId === id);

      if (customizeAlcohol) {
        return customizeAlcohol.selected;
      }

      const isAddon = packageAddons.state['AlcoholIncluded'];

      if (!isAddon) {
        return false;
      }

      let travelDate = '';

      switch (kind) {
        case ProcessType.TRIPABROAD: {
          const formData = localStorage.getItem(PersistStorageKeys.FormsTripAbroad);
          travelDate = (formData ? (JSON.parse(formData) as FormsTripAbroad) : null)?.travelDates.dateFrom || new Date().toString();
          break;
        }
        case ProcessType.TRAVELINPL: {
          const formData = localStorage.getItem(PersistStorageKeys.FormsTravelInPl);
          travelDate = (formData ? (JSON.parse(formData) as FormsTravelInPl) : null)?.travelDates.dateFrom || new Date().toString();
          break;
        }
        case ProcessType.RESIGNATION: {
          const formData = localStorage.getItem(PersistStorageKeys.FormsResignation);
          travelDate = (formData ? (JSON.parse(formData) as FormsResignation) : null)?.travelDates.dateFrom || new Date().toString();
          break;
        }
      }

      return getAge(birthYear.toString(), travelDate) >= LEGAL_AGE;
    };

    return Object.values(insureds).map(({ id, birthYear }) => ({ id, selected: isAlcoholIncluded(id, birthYear) }));
  },
);

export const selectCustomizeForTheActiveIncluded = createSelector(
  ({ process }: AppState) => process,
  selectPremiumCalculationInsureds,
  selectQuotationInsureds,
  ({ customizeForTheActiveIncluded, packageAddons }: fromProcess.State, premiumInsureds, quotationInsureds): PersonsWith[] | null => {
    if (!premiumInsureds && !quotationInsureds) {
      return null;
    }

    const insureds = quotationInsureds ?? premiumInsureds;

    const isForTheActiveIncluded = (id: number): boolean => {
      const customizeForTheActive = customizeForTheActiveIncluded?.find(({ id: selectedId }) => selectedId === id);

      if (packageAddons.state['ForTheActive'] && (!customizeForTheActiveIncluded || !customizeForTheActiveIncluded.length)) {
        return true;
      }

      if (customizeForTheActive) {
        return customizeForTheActive.selected;
      }

      return false;
    };

    return Object.values(insureds).map(({ id }) => ({ id, selected: isForTheActiveIncluded(id) }));
  },
);

export const selectRawCustomizeForTheActiveIncluded = createSelector(
  ({ process }: AppState) => process,
  ({ customizeForTheActiveIncluded }: fromProcess.State) => customizeForTheActiveIncluded,
);

export const selectRawCustomizeAlcoholIncluded = createSelector(
  ({ process }: AppState) => process,
  ({ customizeAlcoholIncluded }: fromProcess.State) => customizeAlcoholIncluded,
);

export const selectAgentSection = createSelector(
  ({ process }: AppState) => process,
  ({ agentSection }: fromProcess.State) => agentSection,
);

export const selectPackageAddons = createSelector(
  ({ process }: AppState) => process,
  ({ packageAddons }: fromProcess.State) => packageAddons,
);

export const selectProcessAddons = createSelector(
  (state: AppState) => state.process,
  selectCustomizeAlcoholIncluded,
  selectCustomizeForTheActiveIncluded,
  ({ packageAddons }: fromProcess.State, customizeAlcoholIncluded, customizeForTheActiveIncluded): CalculationAddOns | null =>
    formatApiAddOns(customizeAlcoholIncluded, customizeForTheActiveIncluded, packageAddons),
);

export const selectPremiumCalculation = createSelector(
  ({ process }: AppState) => process,
  selectProcessKind,
  selectCustomizeAlcoholIncluded,
  selectCustomizeForTheActiveIncluded,
  selectAgentSection,
  selectOfferSelectedPackage,
  selectSelectedPackageName,
  selectOfferDiscountCode,
  selectPackageAddons,
  (
    {}: fromProcess.State,
    processKind,
    selectedCustomizeAlcoholIncluded,
    selectedCustomizeForTheActiveIncluded,
    selectedAgentSection,
    selectedPackage,
    selectedPackageName,
    discountCode,
    addons,
  ) => ({
    processKind,
    selectedCustomizeAlcoholIncluded,
    selectedCustomizeForTheActiveIncluded,
    selectedAgentSection,
    selectedPackage,
    selectedPackageName,
    discountCode,
    addons,
  }),
);

export const selectPolicyDataState = createSelector(
  ({ process }: AppState) => process,
  ({ usePolicyDataState }: fromProcess.State) => usePolicyDataState,
);

export const selectPolicyData = createSelector(
  ({ process }: AppState) => process,
  ({ usePolicyData }: fromProcess.State) => usePolicyData,
);

export const selectIsOfferPriceBoxVisible = createSelector(
  ({ process }: AppState) => process,
  ({ isOfferPriceBoxVisible }: fromProcess.State) => isOfferPriceBoxVisible,
);

export const selectKnfLimit = createSelector(
  ({ process }: AppState) => process,
  ({ knfLimit }: fromProcess.State) => knfLimit,
);

export const selectKnfLimitState = createSelector(
  ({ process }: AppState) => process,
  ({ knfLimitState }: fromProcess.State) => knfLimitState,
);

export const selectBlockListState = createSelector(
  ({ process }: AppState) => process,
  ({ blockListCheckState }: fromProcess.State) => blockListCheckState,
);
